// frontend/src/context/NotificationContext.js
import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
// Global snackbar + confirm dialog state lives here per decision D17
// (reuse NotificationContext rather than introducing a new provider).
// The visual host (Snackbar + ConfirmDialog) is rendered by GlobalUiHost so
// that it sits under the MUI ThemeProvider; this module only owns the state
// and the imperative helpers. Wave 1 note: additive — existing hand-rolled
// snackbars / window.confirm call sites are migrated in a later wave.
import { useAuth } from './AuthContext';
import { API_URL } from '../config';
import { logger } from '../utils/logger';
import { isOfflineErrorSuppressed, offlineNoticeIfDue } from '../offline/offlineNotice';
import {
  isPushSupported,
  isPushSupportedAsync,
  isSubscribed as checkIsSubscribed,
  subscribeToPush,
  unsubscribeFromPush,
  requestPermission,
  getPermissionStatus,
  sendTestNotification
} from '../services/pushNotifications';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

// Convenience hook for the global snackbar surface (D17).
// Returns { showSnackbar, closeSnackbar, snackbar }.
export const useGlobalSnackbar = () => {
  const { showSnackbar, closeSnackbar, snackbar } = useContext(NotificationContext);
  return { showSnackbar, closeSnackbar, snackbar };
};

// Convenience hook returning the imperative confirm() helper (D17).
// Usage: const confirm = useConfirm(); if (await confirm({ message, ... })) {...}
export const useConfirm = () => useContext(NotificationContext).confirm;

export const NotificationProvider = ({ children }) => {
  const { currentUser, authFetch, token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Global snackbar state (D17: reuse this context, not a new provider)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Global confirm-dialog state driven by the imperative confirm() helper.
  // resolveRef holds the pending Promise resolver so a click on
  // Confirm/Cancel resolves the Promise the caller awaited.
  const [confirmState, setConfirmState] = useState({ open: false });
  const confirmResolveRef = useRef(null);

  const showSnackbar = useCallback((message, severity = 'success') => {
    if (isOfflineErrorSuppressed(severity)) return;
    setSnackbar({ open: true, message, severity });
  }, []);

  // Single global source of the calm "you're offline" notice: fired once per
  // real network failure (see AuthContext's authFetch -> noteNetworkError),
  // regardless of which snackbar mechanism the failing call site uses
  // (this context, the local useSnackbar hook, or EmailPage's own wrapper).
  useEffect(() => {
    const onOfflineNotice = () => {
      const notice = offlineNoticeIfDue(Date.now());
      if (notice) setSnackbar({ open: true, message: notice.message, severity: notice.severity });
    };
    window.addEventListener('app:offline-notice', onOfflineNotice);
    return () => window.removeEventListener('app:offline-notice', onOfflineNotice);
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  // Imperative confirm: returns a Promise<boolean> that resolves true when the
  // user confirms and false when they cancel/dismiss. Accepts either a message
  // string or an options object {title, message, confirmText, cancelText,
  // confirmColor}.
  const confirm = useCallback((options = {}) => {
    const opts = typeof options === 'string' ? { message: options } : options;
    return new Promise((resolve) => {
      confirmResolveRef.current = resolve;
      setConfirmState({ ...opts, open: true });
    });
  }, []);

  const resolveConfirm = useCallback((result) => {
    setConfirmState((prev) => ({ ...prev, open: false }));
    const resolve = confirmResolveRef.current;
    confirmResolveRef.current = null;
    if (resolve) resolve(result);
  }, []);

  const handleConfirm = useCallback(() => resolveConfirm(true), [resolveConfirm]);
  const handleCancelConfirm = useCallback(() => resolveConfirm(false), [resolveConfirm]);

  // Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  const authFetchRef = useRef(authFetch);
  authFetchRef.current = authFetch;

  const fetchNotifications = useCallback(async (unreadOnly = true, limit = 20) => {
    if (!currentUser) return;

    setLoading(true);
    try {
      const response = await authFetchRef.current(
        `${API_URL}/api/notifications?unread=${unreadOnly}&limit=${limit}`
      );

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications);
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      const response = await authFetchRef.current(
        `${API_URL}/api/notifications/${notificationId}/read`,
        { method: 'POST' }
      );

      if (response.ok) {
        // Remove from list (we show unread only)
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const response = await authFetchRef.current(
        `${API_URL}/api/notifications/read-all`,
        { method: 'POST' }
      );

      if (response.ok) {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  const clearRead = useCallback(async () => {
    try {
      const response = await authFetchRef.current(
        `${API_URL}/api/notifications/clear-read`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        // Remove read notifications from local state
        setNotifications(prev => prev.filter(n => !n.read));
      }
    } catch (error) {
      console.error('Error clearing read notifications:', error);
    }
  }, []);

  // Check push notification support and status
  const checkPushStatus = useCallback(async () => {
    // First do a quick sync check
    let supported = isPushSupported();

    // If not supported, try the async check which waits for service worker
    // This is more reliable on iOS where PushManager may only be available after SW registration
    if (!supported) {
      logger.debug('[NotificationContext] Sync check failed, trying async check...');
      supported = await isPushSupportedAsync();
    }

    setPushSupported(supported);
    logger.debug('[NotificationContext] Push supported:', supported);

    if (supported) {
      setPushPermission(getPermissionStatus());
      try {
        const subscribed = await checkIsSubscribed();
        setPushSubscribed(subscribed);
      } catch (err) {
        // Expected on localhost or when service worker isn't fully ready
        setPushSubscribed(false);
      }
    }
  }, []);

  // Subscribe to push notifications
  const subscribePush = useCallback(async () => {
    logger.debug('[NotificationContext] subscribePush called, token:', !!token, 'loading:', pushLoading);

    if (!token) {
      console.error('[NotificationContext] No auth token available');
      return { success: false, error: 'Not authenticated' };
    }

    if (pushLoading) {
      logger.debug('[NotificationContext] Already loading, skipping');
      return { success: false, error: 'Already in progress' };
    }

    setPushLoading(true);
    try {
      // First request permission if needed
      const permission = getPermissionStatus();
      logger.debug('[NotificationContext] Current permission:', permission);

      if (permission === 'default') {
        logger.debug('[NotificationContext] Requesting permission...');
        const permResult = await requestPermission();
        logger.debug('[NotificationContext] Permission result:', permResult);
        setPushPermission(permResult.permission);
        if (!permResult.success) {
          setPushLoading(false);
          return { success: false, error: 'Permission denied' };
        }
      } else if (permission === 'denied') {
        setPushLoading(false);
        return { success: false, error: 'Permission blocked. Please enable in browser settings.' };
      }

      logger.debug('[NotificationContext] Calling subscribeToPush...');
      const result = await subscribeToPush(token);
      logger.debug('[NotificationContext] subscribeToPush result:', result);

      if (result.success) {
        setPushSubscribed(true);
        logger.debug('[NotificationContext] Successfully subscribed!');
      } else {
        console.error('[NotificationContext] Subscription failed:', result.error);
      }
      return result;
    } catch (error) {
      console.error('[NotificationContext] Error subscribing to push:', error);
      return { success: false, error: error.message };
    } finally {
      setPushLoading(false);
    }
  }, [token, pushLoading]);

  // Unsubscribe from push notifications
  const unsubscribePush = useCallback(async () => {
    if (!token || pushLoading) return { success: false };

    setPushLoading(true);
    try {
      const result = await unsubscribeFromPush(token);
      if (result.success) {
        setPushSubscribed(false);
      }
      return result;
    } catch (error) {
      console.error('Error unsubscribing from push:', error);
      return { success: false, error: error.message };
    } finally {
      setPushLoading(false);
    }
  }, [token, pushLoading]);

  // Send test push notification
  const testPush = useCallback(async () => {
    if (!token) return { success: false, error: 'Not authenticated' };
    return sendTestNotification(token);
  }, [token]);

  // Fetch notifications on component mount and when user changes
  const checkPushStatusRef = useRef(checkPushStatus);
  checkPushStatusRef.current = checkPushStatus;
  const fetchNotificationsRef = useRef(fetchNotifications);
  fetchNotificationsRef.current = fetchNotifications;

  useEffect(() => {
    if (currentUser) {
      fetchNotificationsRef.current();
      checkPushStatusRef.current();

      // Set up polling to check for new notifications
      const interval = setInterval(() => {
        fetchNotificationsRef.current();
      }, 60000); // Check every minute

      return () => clearInterval(interval);
    }
  }, [currentUser]);

  const value = useMemo(() => ({
    // Existing notification values
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearRead,
    // Global snackbar (D17)
    snackbar,
    showSnackbar,
    closeSnackbar,
    // Global confirm dialog (D17)
    confirm,
    confirmState,
    handleConfirm,
    handleCancelConfirm,
    // Push notification values
    pushSupported,
    pushPermission,
    pushSubscribed,
    pushLoading,
    subscribePush,
    unsubscribePush,
    testPush,
    checkPushStatus,
  }), [notifications, unreadCount, loading, fetchNotifications, markAsRead,
    markAllAsRead, clearRead, snackbar, showSnackbar, closeSnackbar,
    confirm, confirmState, handleConfirm, handleCancelConfirm,
    pushSupported, pushPermission, pushSubscribed,
    pushLoading, subscribePush, unsubscribePush, testPush, checkPushStatus]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
